import { programCdToLabel } from "../sharedUtils"

const PROGRAM_KEYS = { SPP: "spp", CPP3: "cpp", MPP3: "mpp" }

const LEGACY_STATUS_CODES = {
  APPLIED: "APPL",
  APPROVED: "APPR",
  COMPLETE: "COMP",
  DENIED: "DEN",
  EXPIRED: "EXP",
  INTERESTED: "INT",
  IN_PROGRESS: "PROG",
}

const REQUIREMENT_REASON_CODES = {
  COMPLETE: "COMP",
  IN_PROGRESS: "IN_PROGRESS",
  OPEN: null,
}

export const toLegacyStatusCode = status => LEGACY_STATUS_CODES[status] ?? null

const toLegacyRequirement = requirement => ({
  requireId: requirement.requireId,
  levelCd: requirement.levelCode,
  reasonCd: REQUIREMENT_REASON_CODES[requirement.status] ?? null,
  completeDate: requirement.completionDate ?? null,
})

const toLegacyActivity = activity => ({
  availableCd: activity.availableCode,
  levelCd: activity.levelCode,
  reasonCd: activity.reasonCode ?? null,
  completeDate: activity.completeDate ?? null,
})

const toLegacyLevel = level => ({
  levelCd: level.levelCode,
  completeCd: level.completeCode ?? null,
  startDate: level.startDate ?? null,
  endDate: level.endDate ?? null,
})

const toLegacyCpeHour = hour => ({
  id: hour.id,
  courseTitle: hour.description ?? null,
  hours: hour.approvedAmount ?? hour.requestedAmount ?? 0,
  date: hour.endDate ?? hour.startDate ?? null,
  careerPath: hour.careerPathCode ?? null,
  approvalStatus: hour.status ?? null,
})

const toLegacyHistoryRecord = record => ({
  programCode: record.programCode,
  programName: record.programName ?? programCdToLabel[record.programCode],
  programStatusCode: toLegacyStatusCode(record.status),
  programStatusStartDate: record.statusStartDate ?? null,
  programStatusEndDate: record.statusEndDate ?? null,
  currentStatus: record.current ? "Y" : "N",
  careerPathCode: record.careerPath?.code ?? null,
  careerPathName: record.careerPath?.name ?? null,
})

const toLegacyProgress = (programCode, program) => {
  if (!program?.progress) return null
  const { progress } = program
  const subscription = progress.latestSubscription
  return {
    programStatusCd: toLegacyStatusCode(program.status),
    programCd: programCode,
    careerPathCd: program.careerPath?.code ?? null,
    levelCd: progress.levelCode ?? null,
    nextLevelCd: progress.nextLevelCode ?? null,
    levelStartDate: progress.levelStartDate ?? null,
    levelEndDate: progress.levelEndDate ?? null,
    latestSubscriptionStartDate: subscription?.startDate ?? null,
    latestSubscriptionEndDate: subscription?.endDate ?? null,
    latestSubscriptionExpired: subscription?.expired ?? null,
  }
}

const toLegacyApplicationInfo = program => {
  const { application } = program || {}
  if (!application) return null
  return {
    applicationId: application.applicationId ?? null,
    latestApplicationStatusCode: toLegacyStatusCode(application.status),
    applicationStatusStartDate: application.statusStartDate ?? null,
    careerPathCode: application.careerPath?.code ?? null,
    careerPathName: application.careerPath?.name ?? null,
  }
}

const toCareerPathChoices = options =>
  (options || []).map(({ careerPath, mappedFrom }) => ({
    careerPathCode: careerPath.code,
    careerPathName: careerPath.name,
    isMapped: Boolean(mappedFrom) && mappedFrom.code !== careerPath.code,
    originalLabel: mappedFrom?.name ?? careerPath.name,
    originalCode: mappedFrom?.code ?? careerPath.code,
  }))

export const hasBlocker = (eligibility, blocker) =>
  Boolean(eligibility?.blockers?.includes(blocker))

// SPP members can have levels without a tx_progstatus row, so the server's
// canViewProgress stands in for a missing status.
const getSppStatusCode = spp => {
  const statusCode = toLegacyStatusCode(spp?.status)
  if (statusCode) return statusCode
  if (!spp?.eligibility?.canViewProgress) return null
  if (hasBlocker(spp.eligibility, "ALREADY_COMPLETE")) return "COMP"
  return spp.eligibility.canRenew ? "EXP" : "PROG"
}

const getProgramStatusCode = (programCode, program) =>
  programCode === "SPP"
    ? getSppStatusCode(program)
    : toLegacyStatusCode(program?.status)

const isCardStatus = statusCode => ["PROG", "EXP"].includes(statusCode)

const toLegacyCurrentProgram = (programCode, program) => {
  const programStatusCode = getProgramStatusCode(programCode, program)
  if (!isCardStatus(programStatusCode)) return null
  return {
    programCode,
    programName: programCdToLabel[programCode],
    programStatusCode,
    programStatusStartDate: program.statusStartDate ?? null,
    programStatusEndDate: program.statusEndDate ?? null,
    currentStatus: "Y",
    careerPathCode: program.careerPath?.code ?? null,
    careerPathName: program.careerPath?.name ?? null,
    programProgressStartDate: program.progressStartDate ?? null,
  }
}

const hasValue = value => value !== undefined

// Server eligibility for a program code (SPP, CPP3, or MPP3), or null when the
// member was not loaded from the Membership API.
export const getEligibility = (me, programCode) =>
  me?.lifelongLearning?.[PROGRAM_KEYS[programCode]]?.eligibility ?? null

// Reshapes a Membership `me.lifelongLearning` response into the legacy `me`
// view model the Education screens render. Only the fields a screen queried
// are present; eligibility stays on `me.lifelongLearning` for the server's rules.
export const adaptLifelongLearningMe = me => {
  if (!me) return me
  const ll = me.lifelongLearning || {}
  const { spp, cpp, mpp } = ll
  const education = {}

  if (hasValue(cpp?.status)) {
    education.currentCppProgramStatus = toLegacyStatusCode(cpp.status)
  }
  if (hasValue(mpp?.status)) {
    education.currentMppProgramStatus = toLegacyStatusCode(mpp.status)
  }
  if (cpp?.careerPath !== undefined) {
    education.currentCppCareerPath = cpp.careerPath?.code ?? null
  }
  if (mpp?.careerPath !== undefined) {
    education.currentMppCareerPath = mpp.careerPath?.code ?? null
  }
  if (ll.levels) education.levels = ll.levels.map(toLegacyLevel)
  if (ll.requirements) {
    education.requirements = ll.requirements.map(toLegacyRequirement)
  }
  if (ll.availableActivities) {
    education.availableActivities = ll.availableActivities.map(toLegacyActivity)
  }
  if (ll.cpeHours) education.cpeHours = ll.cpeHours.map(toLegacyCpeHour)
  if (ll.programHistory) {
    education.programHistory = ll.programHistory
      .filter(record => record.status === "COMPLETE")
      .map(toLegacyHistoryRecord)
  }

  if (spp || cpp || mpp) {
    education.currentPrograms = [
      ["SPP", spp],
      ["CPP3", cpp],
      ["MPP3", mpp],
    ]
      .filter(([, program]) => program)
      .map(([programCode, program]) =>
        toLegacyCurrentProgram(programCode, program),
      )
      .filter(Boolean)
  }
  if (cpp?.progress) {
    education.cppProgramProgress = toLegacyProgress("CPP3", cpp)
  }
  if (mpp?.progress) {
    education.mppProgramProgress = toLegacyProgress("MPP3", mpp)
  }
  if (cpp?.application !== undefined) {
    education.currentCppApplicationInfo = toLegacyApplicationInfo(cpp)
  }
  if (mpp?.application !== undefined) {
    education.currentMppApplicationInfo = toLegacyApplicationInfo(mpp)
  }
  if (cpp?.applicationCareerPathOptions) {
    education.cppCareerPathChoices = toCareerPathChoices(
      cpp.applicationCareerPathOptions,
    )
  }
  if (mpp?.applicationCareerPathOptions) {
    education.mppCareerPathChoices = toCareerPathChoices(
      mpp.applicationCareerPathOptions,
    )
  }

  return {
    ...me,
    id: me.pgaId,
    education,
  }
}

export const getSppExpiredDate = me => {
  const spp = me?.lifelongLearning?.spp
  return (
    spp?.statusEndDate ?? spp?.progress?.latestSubscription?.endDate ?? null
  )
}
